import styled from "@emotion/styled";

interface SegPatenteInputProps {
  value: string;
  /** Recibe el valor ya normalizado: mayúsculas, sin separadores y hasta 7. */
  onChange: (value: string) => void;
  onBlur?: () => void;
  leyendaError?: string;
  autoFocus?: boolean;
  /** Color del foco, para marca blanca. */
  focusColor?: string;
  name?: string;
  /**
   * Ayuda con los dos formatos vigentes debajo del campo. Se apaga donde el
   * espacio manda y el placeholder ya alcanza, como en el hero de la home.
   */
  mostrarFormatos?: boolean;
}

const INK = "#10151f";
const DANGER = "#d1424f";
/** Azul de la banda Mercosur de la chapa argentina, tomado de una chapa real. */
const AZUL_MERCOSUR = "#011590";
const LARGO_MAXIMO = 7;

/** Los dos formatos vigentes de la patente argentina. */
const FORMATOS = ["AB123CD", "ABC123"];

/** Mayúsculas, sin espacios ni guiones, y cortada al largo de una patente. */
export const normalizarPatente = (valor: string): string =>
  valor
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, LARGO_MAXIMO);

const Contenedor = styled.div`
  text-align: center;
`;

const Chapa = styled.div<{ conError: boolean; focusColor: string }>`
  width: min(290px, 100%);
  margin: 0 auto;
  background: #fff;
  border: 2px solid ${({ conError }) => (conError ? DANGER : INK)};
  border-radius: 9px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(16, 21, 31, 0.06),
    0 12px 32px -12px rgba(16, 21, 31, 0.18);
  transition: border-color 0.18s, box-shadow 0.18s;

  &:focus-within {
    border-color: ${({ conError, focusColor }) => (conError ? DANGER : focusColor)};
    box-shadow: 0 0 0 4px
      ${({ conError, focusColor }) =>
        conError ? "rgba(209, 66, 79, 0.18)" : `${focusColor}2e`};
  }
`;

const Banda = styled.div<{ conError: boolean }>`
  background: ${({ conError }) => (conError ? DANGER : AZUL_MERCOSUR)};
  color: #fff;
  font-size: 10px;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  padding: 4px 0 3px;
  font-weight: 500;
  transition: background 0.18s;
`;

const Campo = styled.input`
  width: 100%;
  border: 0;
  background: transparent;
  text-align: center;
  font-family: "DM Mono", "Cascadia Mono", ui-monospace, "SF Mono", Menlo,
    Consolas, monospace;
  font-size: 32px;
  line-height: 1;
  letter-spacing: 0.12em;
  padding: 8px 8px 10px;
  text-transform: uppercase;
  font-weight: 500;
  color: ${INK};

  &::placeholder {
    color: #c9d0dc;
    letter-spacing: 0.12em;
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 380px) {
    font-size: 28px;
  }
`;

const Error = styled.p`
  color: ${DANGER};
  font-size: 13px;
  font-weight: 500;
  margin: 3px 0 0;
  min-height: 18px;
`;

const Formatos = styled.p`
  font-size: 11px;
  color: #7a8394;
  margin: 4px 0 0;
  line-height: 1.3;

  code {
    background: #eef1f6;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 11px;
    font-family: "DM Mono", ui-monospace, Consolas, monospace;
    color: inherit;
  }
`;

/**
 * Input con forma de chapa patente argentina: la banda azul del Mercosur
 * arriba, el marco negro y el número en monoespaciada grande. Normaliza lo que
 * se tipea (mayúsculas, sin espacios ni guiones, 7 caracteres) para que entre
 * siempre en alguno de los dos formatos vigentes, AB123CD o ABC123.
 *
 * Es específico de Argentina: otros países usan formatos distintos y el campo
 * común (SegInput) los sigue atendiendo.
 */
export const SegPatenteInput = ({
  value,
  onChange,
  onBlur,
  leyendaError,
  autoFocus = false,
  focusColor = "#2c7dfd",
  name = "patente",
  mostrarFormatos = true,
}: SegPatenteInputProps) => {
  const conError = !!leyendaError;

  return (
    <Contenedor>
      <Chapa conError={conError} focusColor={focusColor}>
        <Banda conError={conError}>Argentina</Banda>
        <Campo
          id={name}
          name={name}
          type="text"
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          maxLength={LARGO_MAXIMO}
          placeholder="AB123CD"
          aria-label="Patente"
          aria-invalid={conError}
          autoFocus={autoFocus}
          value={value}
          onBlur={onBlur}
          onChange={(e) => onChange(normalizarPatente(e.target.value))}
        />
      </Chapa>

      {mostrarFormatos && (
        <Formatos>
          Sirven los dos formatos:{" "}
          {FORMATOS.map((formato, i) => (
            <span key={formato}>
              {i > 0 && " y "}
              <code>{formato}</code>
            </span>
          ))}
        </Formatos>
      )}

      <Error role="alert">{leyendaError}</Error>
    </Contenedor>
  );
};
